<template>
    <b-modal id="note-history-modal" title="Note History" size="lg" @show="loadHistory" hide-footer no-fade>
        <div v-if="loading" class="text-center py-4">
            <b-spinner variant="primary"></b-spinner>
            <div>Loading history...</div>
        </div>

        <div v-else-if="history.length === 0" class="text-center text-muted py-4">
            No edit history found for this note.
        </div>

        <div v-else>
            <div v-for="(item, index) in history" :key="index" class="history-item mb-3">
                <div class="history-header d-flex justify-content-between align-items-center mb-2">
                    <div>
                        <strong>{{ formatName(item.updatedBy) }}</strong>
                        <span class="text-muted ml-2">{{ formatDate(item.updated) }}</span>
                    </div>
                    <b-badge v-if="index === 0" variant="primary">Current Version</b-badge>
                    <b-badge v-else variant="info">Version {{ history.length - index }}</b-badge>
                </div>

                <div class="history-content">
                    <div v-if="item.parsedNote">
                        <div v-if="item.parsedNote.ActivityType !== null && item.parsedNote.ActivityType !== undefined">
                            <b-badge v-if="item.parsedNote.ActivityType === 0" variant="primary" class="mb-2">
                                <i class="fa fa-phone mr-1"></i> Phone Call
                            </b-badge>
                            <b-badge v-else-if="item.parsedNote.ActivityType === 1" variant="primary" class="mb-2">
                                <i class="fa fa-comment-dots mr-1"></i> Text Message
                            </b-badge>
                            <b-badge v-else-if="item.parsedNote.ActivityType === 2" variant="primary" class="mb-2">
                                <i class="fa fa-at mr-1"></i> Electronic
                            </b-badge>
                            <b-badge v-else-if="item.parsedNote.ActivityType === 3" variant="primary" class="mb-2">
                                <i class="fa fa-wifi mr-1"></i> Portal
                            </b-badge>
                            <b-badge v-else-if="item.parsedNote.ActivityType === 4" variant="primary" class="mb-2">
                                <i class="fa fa-fax mr-1"></i> Fax
                            </b-badge>
                        </div>
                        <div class="note-body-text">{{ item.parsedNote.Body }}</div>
                    </div>
                    <div v-else class="text-muted">
                        <em>Unable to parse note content</em>
                    </div>
                </div>
            </div>
        </div>
    </b-modal>
</template>

<script lang="ts">
import Vue from 'vue';
import { Component, Prop } from 'vue-property-decorator';
import axios from 'axios';
import moment from 'moment';
import { NotificationOptions } from "@/util/NotificationOptionsPresets";

interface NoteHistory {
    id: number;
    updated: Date;
    updatedBy: string;
    oldValue: string | null;
    newValue: string;
    parsedNote?: any;
}

@Component({
    name: 'NoteHistoryModal'
})
export default class NoteHistoryModal extends Vue {
    @Prop({ required: true }) private noteId!: number;

    private history: NoteHistory[] = [];
    private loading = false;

    async loadHistory() {
        this.loading = true;
        try {
            const response = await axios.get(`/Note/${this.noteId}/history`);
            this.history = response.data;

            // Parse JSON in newValue for display
            this.history.forEach(item => {
                try {
                    const parsed = JSON.parse(item.newValue);
                    if (Array.isArray(parsed) && parsed.length > 0 && parsed[0] && parsed[0].Body) {
                        item.parsedNote = parsed[0];
                    }
                } catch (e) {
                    console.error('Error parsing note history:', e);
                }
            });
        } catch (error) {
            console.error('Error loading note history:', error);
            this.$notification(NotificationOptions.error("Failed to load note history"));
        } finally {
            this.loading = false;
        }
    }

    formatName(name: string) {
        if (name.includes("/")) return name.split("/")[1];
        if (name.includes("\\")) return name.split("\\")[1];
        return name.split("@")[0];
    }

    formatDate(date: Date) {
        return moment.utc(date).local().format("LLL");
    }
}
</script>

<style scoped lang="scss">
.history-item {
    padding: 15px;
    border: 1px solid rgba(0, 0, 0, 0.1);
    border-radius: 8px;
    background: rgba(0, 0, 0, 0.02);
}

.history-header {
    padding-bottom: 10px;
    border-bottom: 1px solid rgba(0, 0, 0, 0.1);
}

.history-content {
    padding-top: 10px;
}

.note-body-text {
    white-space: pre-wrap;
    line-height: 1.5;
}
</style>
